/**
 * Ordem de empilhamento das camadas no mapa. Todos os módulos devem usar
 * estas constantes em `layer.setZIndex(...)`.
 */
export const Z = {
  /** Camadas GOES: GOES_BASE + posição na pilha (0, 1, 2). */
  GOES_BASE: 0,
  /**
   * Foto de satélite do fundo (EOxCloudless, só depois do zoom máximo do GOES):
   * ABAIXO de tudo, até da imagem do GOES, que vai ficando transparente ao
   * aproximar e a revela.
   */
  FUNDO: -1,
  /** Ruas e nomes do fundo (OpenStreetMap, desenhados pela EOX): sobre a foto, ainda abaixo do GOES. */
  FUNDO_ROTULOS: -0.5,
  /**
   * Previsão de modelo do "Próximos dias" (`src/previsao`): logo acima das
   * imagens do satélite. Só aparece no modo, quando as camadas observadas de
   * evento (rastreio, radar, fumaça do GOES, qualidade do ar, focos) estão ocultas.
   */
  PREVISAO: 2.5,
  /**
   * Chuva por satélite (GSMaP, `src/gsmap`): logo acima das imagens e abaixo
   * do rastreio (o contorno da nuvem continua legível por cima da chuva) e
   * do radar, que mede de perto o que o satélite estima de longe.
   */
  GSMAP: 2.7,
  /**
   * Previsão e rastreio de nuvens (pyForTraCC), subcamadas do Satélite: logo
   * acima das imagens (a pilha vai até GOES_BASE + 2) e abaixo do radar. O
   * rastreio, que é observação, fica por cima da previsão.
   */
  TRACKING_FORECAST: 3,
  /**
   * Rastreio da chuva (pyForTraCC sobre o GSMaP): acima da previsão e abaixo
   * do contorno da nuvem — com os dois ligados, o toque dentro de um sistema
   * de nuvens abre a ficha dele; fora dele, a da área de chuva (e a lista do
   * painel leva a qualquer uma).
   */
  TRACKING_CHUVA: 3.5,
  TRACKING: 4,
  /**
   * Trajetória do sistema selecionado (a passada e a prevista, com os
   * horários): por cima dos dois contornos, que ela atravessa, e ainda abaixo
   * do radar.
   */
  TRACKING_TRAJETORIA: 4.5,
  /**
   * Radares do SIPAM: logo ACIMA das imagens do satélite (o eco precisa ficar
   * por cima da nuvem para ser visto) e abaixo de todo vetor.
   */
  RADAR: 5,
  /**
   * Contorno da fumaça (NOAA): acima do radar — a pluma cobre áreas maiores e
   * o radar por baixo continua legível pelo preenchimento translúcido — e
   * abaixo de todo vetor, que continua por cima como referência.
   */
  SMOKE: 6,
  /**
   * Raios do GLM: pontos pequenos, acima da fumaça e das linhas do mapa (um
   * limite de município não os esconde) e abaixo da máscara da busca e de
   * todo ponto de estação e foco (o foco é o assunto; o raio, o contexto).
   */
  RAIOS: 33,
  /**
   * Grade do modelo CAMS da qualidade do ar: translúcida, logo acima da
   * fumaça (a pluma continua legível por baixo, e o modelo é contexto
   * regional, não o dado principal) e abaixo da área do AIS e de todo vetor.
   */
  AR_MODELO: 7,
  /** Preenchimentos de polígonos (TIs, UCs, municípios). */
  VECTOR_FILL: 10,
  /** Contornos e linhas (municípios, estados, rios, rodovias). */
  VECTOR_LINE: 20,
  /**
   * Contorno da célula do modelo CAMS com a ficha aberta: acima das linhas
   * (um limite de estado não o esconde) e abaixo de todo ponto.
   */
  AR_MODELO_DESTAQUE: 21,
  /** Célula da ficha do ponto do "Próximos dias": como a do CAMS, acima das linhas e abaixo de todo ponto. */
  PREVISAO_DESTAQUE: 22,
  /** Setas do vento do "Próximos dias": sobre a cor da velocidade, abaixo dos vetores e dos nomes das cidades. */
  PREVISAO_SETAS: 9,
  /** Área onde o AIS é de fato ouvido (embarcações). Abaixo de tudo que é ponto. */
  SHIPS_RANGE: 8,
  /**
   * Município da busca (src/busca): escurece de leve o resto do mapa e contorna o município.
   * Abaixo dos pontos (focos, estações, embarcações) e da máscara do filtro dos focos.
   */
  BUSCA: 34,
  /** Áreas do filtro dos últimos focos: escurece o resto do mapa e contorna as áreas. */
  AREA_FOCUS: 35,
  /**
   * Imóveis do CAR e círculo de 375 m do foco cuja ficha está aberta
   * (src/terreno): acima da máscara do filtro dos focos (senão ficariam
   * escurecidos) e abaixo de todo ponto (o foco continua por cima do contorno).
   */
  TERRENO: 35.5,
  /**
   * Embarcações. Fica ACIMA de `AREA_FOCUS` de propósito: a máscara que
   * escurece o mapa no filtro dos últimos focos apagaria a frota inteira.
   */
  SHIPS: 36,
  /** Trilha da embarcação selecionada, logo abaixo dela. */
  SHIPS_TRACK: 37,
  /**
   * Estações de nível dos rios (ANA). Como as embarcações, fica ACIMA de
   * `AREA_FOCUS`: a máscara do filtro dos últimos focos apagaria os pontos.
   */
  RIVERS: 38,
  /** Estações do CEMADEN, logo abaixo dos focos. */
  STATIONS: 39,
  /** Focos de queimada. */
  FIRES: 40,
  /**
   * Estações meteorológicas (INMET e METAR): ACIMA dos focos e das outras
   * estações pontuais — o número ao lado do símbolo precisa ficar legível por
   * cima deles — e abaixo dos rótulos das cidades. A sondagem usa 41/42.
   */
  METEO: 43,
  /**
   * Qualidade do ar medida (sensores de baixo custo e estações do
   * MonitorAr): ACIMA dos focos — são poucos pontos, e um hexágono coberto
   * por dezenas de chamas na mesma cidade some.
   */
  AR: 44,
  /** Rótulos (nomes das cidades etc.). */
  LABELS: 50,
  /** A localização da pessoa (busca por município, "Usar minha localização"): acima dos rótulos. */
  BUSCA_LOCAL: 55,
  /** Destaques de seleção / hover. */
  HIGHLIGHT: 60,
} as const;
